// @rpg-unit/scene-player: scripted scenes from data (tier 4; IMP-0044, gap G08 of dry run 1), cut from spike 002's
// cutscene module (src/town/cutscene.ts) and made content-only. A scene is a row: an `@rpg/camera` timeline (shots,
// marks, faces, poses, lines, fades, the letterbox, sets, events with arguments, holds), an optional trigger (any
// event, matched on its payload's fields), whether it plays once, and which conversation each hold asks for. It gives a
// game:
//   - scenes played when asked (`story:scene`, or the `scene/play` action) or by their trigger, one at a time, queued,
//     with the world paused while one plays (the kernel's pause; only `whilePaused` systems run);
//   - lines out as the standard `dialogue:line`; events out by their names with their arguments (`item:gained`, a clock
//     move: the game's or the standard ones); sets kept as the feature's flags;
//   - holds: a hold asks for its conversation (`dialogue:request`) and the scene's time stands still until that
//     conversation has opened (`dialogue:open` with its person) and closed (`dialogue:close`), however long the player
//     takes; a talk with the same person already open when the hold asked (queued ahead of it) doesn't release it;
//   - skip to the end state (while a conversation holds the scene: waits, or ends the talk too, by the game's
//     `skipDuringTalk`): the state cues not yet applied and every
//     moved actor on its last mark, so the world after a skip is the world after watching; `story:scene-done` either way;
//   - seen flags, flags, the scene playing and its queue in the save slice; a load re-applies the pause;
//   - a view for the renderer (the shot's camera, the actors, the fade, the letterbox, the lines) and an overlay
//     (`mountSceneOverlay`, overlay.ts: bars, the fade and subtitles).
// Actors and marks come from the content (a lab's puppets) or the game's host (its NPCs and the hero). It meets other
// features only at standard events (@rpg/contracts): it never imports the conversation feature.
import type { StandardEvents } from "@rpg/contracts";
import type { GameModule } from "@rpg/kernel";
import {
  actorsAt,
  advanceScene,
  cameraAt,
  compileTimeline,
  endMarks,
  fadeAt,
  letterboxAt,
  linesAt,
  releaseHold,
  skipScene,
  startScene,
  validateTimeline,
  type CompiledTimeline,
  type Cue,
  type Placed,
  type SceneResolver,
  type SceneState,
  type ShotPose,
  type TimelineJson,
} from "@rpg/camera";

export { mountSceneOverlay, SCENE_CSS } from "./overlay";

/** This unit's version (the manifest's and package.json's). */
export const SCENE_PLAYER_VERSION = "0.1.1" as const;

/** Planted defects beside what they break: a game's scene checks must go red with them on. */
export const SCENE_PLAYER_FAULTS = {
  /** A scene that plays once plays again on its next trigger (the seen flag isn't kept). */
  seenForgotten: false,
  /** A skip drops the rest of the scene's state (its sets, events and the actors' last marks). */
  skipLosesState: false,
  /** A skip during a talk ends the scene but leaves the talk open (no `dialogue:cancel`). */
  skipLeavesTalk: false,
  /** A scene's talk is always asked for static, whatever its own or the game's setting. */
  talkModeDropped: false,
  /** A hold's conversation closing doesn't release the scene: it waits forever. */
  holdNeverReleased: false,
  /** Any `dialogue:close` from the hold's person releases it, even a talk that was open before the hold asked (as in
   *  0.1.0): the scene runs on while the talk it asked for is still to come. */
  holdReleasedByAnyTalk: false,
  /** A skip pressed while a talk holds the scene (on "wait") is dropped instead of applying when the talk ends. */
  waitingSkipDropped: false,
};

/** Whether the player may skip a scene while a conversation inside it is going (the person's option, 2026-10-23):
 *  "wait" (the skip waits until the talk ends, then skips the rest of the scene; the default) or "endTalk" (the skip ends the talk too, with
 *  `dialogue:cancel`, and the scene jumps to its end state). */
export type SkipDuringTalk = "wait" | "endTalk";

/** A talk a scene's hold asks for. */
export interface SceneTalk {
  npc: string;
  script: string;
  knot?: string;
  talk?: "static" | "open";
  topic?: string;
}

/** A scene, as the game writes it. */
export interface SceneRow {
  id: string;
  timeline: TimelineJson;
  /** Play when an event is emitted whose payload has these fields (absent: only when asked). */
  trigger?: { event: string; match?: Readonly<Record<string, string | number | boolean>> };
  /** Play only once (default: true for a triggered scene, false for one asked for). */
  once?: boolean;
  /** The conversation each hold asks for (absent: the hold waits for the `scene/release` action). `talk` "open" asks
   *  for an open talk about `topic` (typed or suggested lines, replies from the conversation feature's provider), the
   *  script its fallback (absent: the player's `talk` setting, else "static"). */
  talks?: Readonly<Record<string, SceneTalk>>;
  /** Pause the world while it plays (default true). */
  pause?: boolean;
  /** Skipping while a talk holds the scene (default: the player's setting, else "wait"). */
  skipDuringTalk?: SkipDuringTalk;
}

/** The content a game passes: its scenes, and (for actors and marks the game doesn't place itself) where they stand. */
export interface SceneContent {
  scenes: readonly SceneRow[];
  marks?: Readonly<Record<string, Placed>>;
  /** Actors the feature places itself (a lab's puppets), where they stand at the start. */
  actors?: Readonly<Record<string, Placed>>;
  /** Lines' words by string id (for `dialogue:line`'s text). */
  strings?: Readonly<Record<string, string>>;
}

/** The game's side, when its world places the actors (its NPCs, the hero): all optional. */
export interface SceneHost {
  actor?(id: string): Placed | null;
  mark?(id: string): Placed | null;
  /** Put an actor on its last mark when a scene ends. */
  putDown?(id: string, at: Placed): void;
  text?(id: string): string;
}

/** What the renderer draws while a scene plays. */
export interface SceneView {
  scene: string;
  t: number;
  /** The shot's camera (null before the first shot). */
  camera: ShotPose | null;
  /** Every actor the scene moves or turns, where it stands now. */
  actors: Record<string, Placed>;
  fade: number;
  letterbox: number;
  /** Lines showing now: speaker and words. */
  lines: { speaker: string; text: string }[];
  /** The hold it waits on, or null. */
  held: string | null;
}

/** The feature's saved state. */
interface PlayerState {
  playing: SceneState | null;
  queue: string[];
  skipAsked: boolean;
  /** Scenes seen (played to the end or skipped). */
  seen: string[];
  /** The scenes' sets. */
  flags: Record<string, string | number | boolean>;
  /** Where the feature's own actors stand (its puppets, after the scenes moved them). */
  placed: Record<string, Placed>;
  /** Poses started in the scene playing: actor, pose, at. */
  poses: { actor: string; pose: string; at: number }[];
  /** The hold's talk: asked for and not yet opened, or open (absent: no talk asked, or a save from 0.1.0, where any
   *  close from its person releases the hold). */
  holdTalk?: "asked" | "open";
  /** A skip pressed while a talk held the scene, on "wait": it applies when the hold releases. */
  skipWaiting?: boolean;
}

/** The feature: its kernel module, its view and its actions by name. */
export interface ScenePlayer {
  /** The kernel module (`id: "scene-player"`). */
  module<M extends object = StandardEvents>(): GameModule<M>;
  view(): SceneView | null;
  active(): boolean;
  seen(): readonly string[];
  flags(): Readonly<Record<string, string | number | boolean>>;
  /** Where an actor the feature places stands now. */
  placed(id: string): Placed | null;
  /** Problems with the content (each named): timelines, triggers, talks. */
  check(): string[];
  readonly actions: { play: string; skip: string; release: string };
}

const ACTIONS = { play: "scene/play", skip: "scene/skip", release: "scene/release" } as const;
/** The kernel pause reason a scene holds. */
export const SCENE_PAUSE = "scene";

/** Make the scene player from content. */
export function createScenePlayer(o: { content: SceneContent; host?: SceneHost; skipDuringTalk?: SkipDuringTalk; talk?: "static" | "open" }): ScenePlayer {
  const c = o.content;
  const host = o.host ?? {};
  const rows = new Map(c.scenes.map((r) => [r.id, r]));
  const compiled = new Map<string, CompiledTimeline>(c.scenes.map((r) => [r.id, compileTimeline(r.timeline)]));
  const st: PlayerState = { playing: null, queue: [], skipAsked: false, seen: [], flags: {}, placed: structuredClone({ ...c.actors }) as Record<string, Placed>, poses: [] };
  const row = (id: string): SceneRow => {
    const r = rows.get(id);
    if (!r) throw new Error(`scene-player: no scene "${id}"`);
    return r;
  };
  const resolver: SceneResolver = {
    actor: (id) => host.actor?.(id) ?? st.placed[id] ?? null,
    mark: (id) => host.mark?.(id) ?? c.marks?.[id] ?? null,
  };
  const text = (id: string): string => host.text?.(id) ?? c.strings?.[id] ?? id;
  let ctxRef: Parameters<GameModule<StandardEvents>["setup"]>[0] | null = null;
  const emit = (name: string, payload: unknown): void => (ctxRef!.events.emit as (n: string, p: unknown) => void)(name, payload);
  const once = (r: SceneRow): boolean => r.once ?? r.trigger !== undefined;
  const ask = (id: string): void => {
    const r = row(id);
    if (once(r) && st.seen.includes(id) && !SCENE_PLAYER_FAULTS.seenForgotten) return;
    if (st.playing?.scene === id || st.queue.includes(id)) return;
    st.queue.push(id);
  };
  const apply = (cues: readonly Cue[], r: SceneRow): void => {
    for (const q of cues) {
      if (q.kind === "line") emit("dialogue:line", { speaker: q.speaker, stringId: q.line, text: text(q.line), seconds: q.seconds });
      else if (q.kind === "pose") st.poses.push({ actor: q.actor, pose: q.pose, at: q.at });
      else if (q.kind === "set") st.flags[q.key] = q.value;
      else if (q.kind === "event") emit(q.name, q.args ?? {});
      else if (q.kind === "hold") {
        const talk = r.talks?.[q.id];
        // the scene's talk, static or open (the person's option, 2026-10-23: a scene's own talk may go open too);
        // marked asked before the request, since a conversation free to open it does so inside the emit
        if (talk) st.holdTalk = "asked";
        if (talk) emit("dialogue:request", { ...talk, talk: SCENE_PLAYER_FAULTS.talkModeDropped ? "static" : (talk.talk ?? o.talk ?? "static") });
      }
    }
  };
  const start = (id: string): void => {
    st.playing = startScene(compiled.get(id)!, resolver);
    st.poses = [];
    st.skipAsked = false;
    delete st.holdTalk;
    delete st.skipWaiting;
    if (row(id).pause ?? true) ctxRef!.pause(SCENE_PAUSE, true);
  };
  const finish = (skipped: boolean): void => {
    const p = st.playing!;
    const tl = compiled.get(p.scene)!;
    if (!(skipped && SCENE_PLAYER_FAULTS.skipLosesState))
      for (const [actor, at] of Object.entries(endMarks(tl, resolver.mark, p, resolver.actor))) {
        if (host.putDown && !(actor in st.placed)) host.putDown(actor, at);
        else st.placed[actor] = at;
      }
    if (!st.seen.includes(p.scene)) st.seen.push(p.scene);
    st.playing = null;
    st.poses = [];
    st.skipAsked = false;
    delete st.holdTalk;
    delete st.skipWaiting;
    ctxRef!.pause(SCENE_PAUSE, false);
    emit("story:scene-done", { scene: p.scene, skipped });
  };
  /** Release the hold `id`; a skip that waited for it applies now. */
  const release = (p: SceneState, id: string): void => {
    if (p.held !== id) return;
    releaseHold(p, id);
    delete st.holdTalk;
    if (st.skipWaiting) st.skipAsked = true;
    delete st.skipWaiting;
  };
  const step = (dt: number): void => {
    if (!st.playing && st.queue.length) start(st.queue.shift()!);
    const p = st.playing;
    if (!p) return;
    const r = row(p.scene);
    const tl = compiled.get(p.scene)!;
    if (st.skipAsked) {
      const out = skipScene(tl, p);
      if (!SCENE_PLAYER_FAULTS.skipLosesState) apply(out.fired, r);
      finish(true);
      return;
    }
    const out = advanceScene(tl, p, dt);
    apply(out.fired, r);
    if (out.finished) finish(false);
  };

  const player: ScenePlayer = {
    actions: ACTIONS,
    active: () => st.playing !== null,
    seen: () => st.seen,
    flags: () => st.flags,
    placed: (id) => st.placed[id] ?? null,
    check() {
      const out: string[] = [];
      const marks = c.marks ? Object.keys(c.marks) : undefined;
      for (const r of c.scenes) {
        for (const p of validateTimeline(r.timeline, marks && !host.mark ? { marks } : {})) out.push(`scene "${r.id}": ${p}`);
        const holds = new Set(r.timeline.steps.flatMap((s) => (s.kind === "hold" ? [s.id] : [])));
        for (const h of Object.keys(r.talks ?? {})) if (!holds.has(h)) out.push(`scene "${r.id}": a talk for hold "${h}", which the timeline lacks`);
        if (r.trigger && !r.trigger.event) out.push(`scene "${r.id}": a trigger with no event`);
      }
      if (rows.size !== c.scenes.length) out.push("scene-player: two scenes with one id");
      return out;
    },
    view() {
      const p = st.playing;
      if (!p) return null;
      const tl = compiled.get(p.scene)!;
      return {
        scene: p.scene,
        t: p.t,
        camera: cameraAt(tl, p.t, p.starts, resolver),
        actors: actorsAt(tl, p.t, p.starts, resolver.mark, resolver.actor),
        fade: fadeAt(tl, p.t),
        letterbox: letterboxAt(tl, p.t),
        lines: linesAt(tl, p.t).map((l) => ({ speaker: l.speaker, text: text(l.line) })),
        held: p.held ?? null,
      };
    },
    module<M extends object = StandardEvents>(): GameModule<M> {
      const m: GameModule<StandardEvents> = {
        id: "scene-player",
        version: SCENE_PLAYER_VERSION,
        // checked declarations (decision 0077): what it emits (a scene's own events are the game's names)
        provides: { events: ["story:scene-done", "dialogue:line", "dialogue:request", "dialogue:cancel"] },
        setup(ctx) {
          ctxRef = ctx;
          ctx.events.on("story:scene", ({ scene }) => ask(scene));
          // the hold's talk opens (at once, or after a talk with the same person that was already open) ...
          ctx.events.on("dialogue:open", ({ npc }) => {
            const p = st.playing;
            if (!p?.held || st.holdTalk !== "asked") return;
            if (row(p.scene).talks?.[p.held]?.npc === npc) st.holdTalk = "open";
          });
          // ... and only its close releases the scene
          ctx.events.on("dialogue:close", ({ npc }) => {
            const p = st.playing;
            if (!p?.held || SCENE_PLAYER_FAULTS.holdNeverReleased) return;
            const talk = row(p.scene).talks?.[p.held];
            if (st.holdTalk === "asked" && !SCENE_PLAYER_FAULTS.holdReleasedByAnyTalk) return;
            if (talk && talk.npc === npc) release(p, p.held);
          });
          for (const r of c.scenes)
            if (r.trigger) {
              const t = r.trigger;
              (ctx.events.on as (n: string, f: (p: unknown) => void) => void)(t.event, (payload) => {
                const pl = (payload ?? {}) as Record<string, unknown>;
                if (Object.entries(t.match ?? {}).every(([k, v]) => pl[k] === v)) ask(r.id);
              });
            }
          // first in the step, and running while the world is paused
          ctx.registerSystem(({ dt }) => step(dt), { name: "scene-player:play", order: -300, whilePaused: true });
          ctx.registerAction(ACTIONS.play, (p) => ask(String((p as { scene: string }).scene)));
          ctx.registerAction(ACTIONS.skip, () => {
            const p = st.playing;
            if (!p) return;
            if (p.held) {
              const r = row(p.scene);
              if ((r.skipDuringTalk ?? o.skipDuringTalk ?? "wait") === "wait") {
                // the skip waits for the talk, then skips the rest
                if (!SCENE_PLAYER_FAULTS.waitingSkipDropped) st.skipWaiting = true;
                return;
              }
              // end the talk the scene holds for, then skip
              const talk = r.talks?.[p.held];
              if (talk && !SCENE_PLAYER_FAULTS.skipLeavesTalk) emit("dialogue:cancel", { npc: talk.npc });
            }
            st.skipAsked = true;
          });
          ctx.registerAction(ACTIONS.release, (p) => {
            if (st.playing) release(st.playing, String((p as { id: string }).id));
          });
          ctx.registerSaveSlice({
            serialize: () => structuredClone(st),
            deserialize(data) {
              Object.assign(st, structuredClone(data as PlayerState));
              // the pause is the kernel's, not saved: a scene in the save holds it again
              ctx.pause(SCENE_PAUSE, st.playing !== null && (row(st.playing.scene).pause ?? true));
            },
          });
        },
      };
      return m as unknown as GameModule<M>;
    },
  };
  return player;
}
